/**
 * Admin PO Inbox — Issue #130 / #106
 *
 * Two queues in one page:
 *   1. PO pending approval — quick [Aprobar] / [Rechazar]
 *   2. PO sent, awaiting receipt — [Recibir]
 *
 * Admin-only. Lives at /compras/inbox.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Container, Card, Table, Button, Badge, Alert, Spinner } from 'react-bootstrap';
import {
  listPendingApprovalPOs,
  listAwaitingReceiptPOs,
  approvePurchaseOrder,
  getMyPurchaseApprovalScope,
  PO_STATUS_LABELS,
  PO_STATUS_VARIANTS,
} from '../../services/purchaseOrderService';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../utils/toast';
import { formatCurrency, formatDate } from '../../utils/formatters';
import ReceivePOModal from '../../components/purchases/ReceivePOModal';
import RejectPOModal from '../../components/purchases/RejectPOModal';
import DashboardLayout from '../../components/layout/DashboardLayout';

const AdminPOInbox = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();

  const [receiveTarget, setReceiveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [approvingId, setApprovingId] = useState(null);

  // Aprobar compras ya no es "solo admin": un admin puede delegarlo, por
  // proyecto, a un líder (Jorge, 2026-07-28). Quién puede qué lo resuelve
  // el servidor — acá solo se pregunta para pintar la pantalla.
  const { data: scope, isLoading: loadingScope } = useQuery({
    queryKey: ['purchase-approval-scope'],
    queryFn: getMyPurchaseApprovalScope,
    staleTime: 60_000,
  });

  const canApproveAny = !!scope?.canApproveAny;
  const delegatedProjectIds = scope?.projectIds || [];

  const {
    data: pendingResp,
    isLoading: loadingPending,
    refetch: refetchPending,
  } = useQuery({
    queryKey: ['po-inbox', 'pending_approval'],
    queryFn: listPendingApprovalPOs,
    enabled: canApproveAny,
  });

  const {
    data: awaitingResp,
    isLoading: loadingAwaiting,
    refetch: refetchAwaiting,
  } = useQuery({
    queryKey: ['po-inbox', 'awaiting_receipt'],
    queryFn: listAwaitingReceiptPOs,
    enabled: canApproveAny && isAdmin,
  });

  // Un líder delegado ve SOLO las compras de los proyectos donde le
  // delegaron. RLS ya lo garantiza del lado de la base; este filtro evita
  // pintar filas que después van a rebotar, que es peor que no verlas.
  const scopePOs = (rows) =>
    isAdmin ? rows : rows.filter((po) => delegatedProjectIds.includes(po.project_id));

  const pendingPOs = scopePOs(pendingResp?.data || []);
  const awaitingPOs = scopePOs(awaitingResp?.data || []);

  const invalidateInbox = () => {
    queryClient.invalidateQueries({ queryKey: ['po-inbox'] });
    queryClient.invalidateQueries({ queryKey: ['purchases'] });
  };

  const handleApprove = async (po) => {
    setApprovingId(po.id);
    const { error } = await approvePurchaseOrder(po.id);
    setApprovingId(null);
    if (error) {
      toast.error('Error al aprobar: ' + error.message);
      return;
    }
    toast.success('PO aprobada');
    refetchPending();
    refetchAwaiting();
    invalidateInbox();
  };

  if (loadingScope) {
    return (
      <Container fluid className="py-4 text-center">
        <Spinner animation="border" size="sm" className="me-2" />
        Cargando...
      </Container>
    );
  }

  if (!canApproveAny) {
    return (
      <Container fluid className="py-4">
        <Alert variant="danger">
          <i className="bi bi-shield-lock me-2"></i>
          Acceso no autorizado. El inbox de compras es para administradores, o
          para líderes a quienes un admin les haya delegado la aprobación de
          compras en la configuración de algún proyecto.
        </Alert>
      </Container>
    );
  }

  const renderStatusBadge = (status) => (
    <Badge bg={PO_STATUS_VARIANTS[status] || 'secondary'}>
      {PO_STATUS_LABELS[status] || status}
    </Badge>
  );

  const renderPendingTable = () => {
    if (loadingPending) {
      return (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" className="me-2" />
          Cargando...
        </div>
      );
    }
    if (pendingPOs.length === 0) {
      return (
        <div className="text-center py-4 text-muted">
          <i className="bi bi-inbox display-6"></i>
          <p className="mt-2 mb-0">No hay PO pendientes de aprobación.</p>
        </div>
      );
    }
    return (
      <div className="table-responsive">
        <Table hover className="align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th>PO</th>
              <th>Fecha</th>
              <th>Proveedor</th>
              <th>Creada por</th>
              <th className="text-end">Total</th>
              <th>Estado</th>
              <th className="text-end">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {pendingPOs.map((po) => (
              <tr key={po.id} style={{ cursor: 'pointer' }}>
                <td onClick={() => navigate(`/compras/${po.id}`)}>
                  <span className="fw-semibold">PO-{po.id.slice(0, 8)}</span>
                </td>
                <td onClick={() => navigate(`/compras/${po.id}`)}>
                  {formatDate(po.po_date || po.created_at)}
                </td>
                <td onClick={() => navigate(`/compras/${po.id}`)}>
                  {po.supplier?.name || '-'}
                </td>
                <td onClick={() => navigate(`/compras/${po.id}`)}>
                  {po.creator?.full_name || '-'}
                </td>
                <td onClick={() => navigate(`/compras/${po.id}`)} className="text-end fw-semibold">
                  {formatCurrency(po.total, po.currency)}
                </td>
                <td onClick={() => navigate(`/compras/${po.id}`)}>{renderStatusBadge(po.status)}</td>
                <td className="text-end">
                  <Button
                    size="sm"
                    variant="success"
                    className="me-2"
                    disabled={approvingId === po.id}
                    onClick={(e) => { e.stopPropagation(); handleApprove(po); }}
                  >
                    {approvingId === po.id ? (
                      <Spinner size="sm" />
                    ) : (
                      <><i className="bi bi-check2-circle me-1"></i>Aprobar</>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={(e) => { e.stopPropagation(); setRejectTarget(po); }}
                  >
                    <i className="bi bi-x-circle me-1"></i>Rechazar
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  const renderAwaitingTable = () => {
    if (loadingAwaiting) {
      return (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" className="me-2" />
          Cargando...
        </div>
      );
    }
    if (awaitingPOs.length === 0) {
      return (
        <div className="text-center py-4 text-muted">
          <i className="bi bi-truck display-6"></i>
          <p className="mt-2 mb-0">No hay PO en tránsito esperando recepción.</p>
        </div>
      );
    }
    return (
      <div className="table-responsive">
        <Table hover className="align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th>PO</th>
              <th>Enviada</th>
              <th>Proveedor</th>
              <th>Esperada</th>
              <th className="text-end">Total</th>
              <th>Estado</th>
              <th className="text-end">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {awaitingPOs.map((po) => (
              <tr key={po.id} style={{ cursor: 'pointer' }}>
                <td onClick={() => navigate(`/compras/${po.id}`)}>
                  <span className="fw-semibold">PO-{po.id.slice(0, 8)}</span>
                </td>
                <td onClick={() => navigate(`/compras/${po.id}`)}>
                  {po.sent_at ? formatDate(po.sent_at) : '-'}
                </td>
                <td onClick={() => navigate(`/compras/${po.id}`)}>
                  {po.supplier?.name || '-'}
                </td>
                <td onClick={() => navigate(`/compras/${po.id}`)}>
                  {po.expected_delivery_date ? formatDate(po.expected_delivery_date) : '-'}
                </td>
                <td onClick={() => navigate(`/compras/${po.id}`)} className="text-end fw-semibold">
                  {formatCurrency(po.total, po.currency)}
                </td>
                <td onClick={() => navigate(`/compras/${po.id}`)}>{renderStatusBadge(po.status)}</td>
                <td className="text-end">
                  <Button
                    size="sm"
                    variant="success"
                    onClick={(e) => { e.stopPropagation(); setReceiveTarget(po); }}
                  >
                    <i className="bi bi-box-arrow-in-down me-1"></i>Recibir
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  return (
    <DashboardLayout>
    <Container fluid className="po-inbox">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
        <div>
          <Button
            variant="link"
            className="p-0 mb-2 text-decoration-none"
            onClick={() => navigate('/compras')}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Volver a compras
          </Button>
          <h2 className="mb-1">
            <i className="bi bi-inboxes me-2"></i>
            Inbox de Compras
          </h2>
          <p className="text-muted mb-0">
            PO esperando tu acción — aprobación o recepción.
          </p>
        </div>
      </div>

      <Card className="shadow-sm mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>
            <i className="bi bi-hourglass-split me-2 text-warning"></i>
            Pendientes de aprobación
          </strong>
          <Badge bg="warning">{pendingPOs.length}</Badge>
        </Card.Header>
        <Card.Body className="p-0">{renderPendingTable()}</Card.Body>
      </Card>

      {/* Recibir mercadería NO está delegado: `receive_purchase` es
          `ensureAdmin` en el carril MCP y la policy delegada sólo cubre
          pending_approval → approved/cancelled. A un líder delegado esta
          cola sólo le ofrecía un botón que devuelve 403. */}
      {isAdmin && (
        <Card className="shadow-sm">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <strong>
              <i className="bi bi-truck me-2 text-info"></i>
              Esperando recepción
            </strong>
            <Badge bg="info">{awaitingPOs.length}</Badge>
          </Card.Header>
          <Card.Body className="p-0">{renderAwaitingTable()}</Card.Body>
        </Card>
      )}

      <ReceivePOModal
        show={!!receiveTarget}
        onHide={() => setReceiveTarget(null)}
        purchase={receiveTarget}
        onReceived={() => {
          setReceiveTarget(null);
          refetchAwaiting();
          invalidateInbox();
        }}
      />
      <RejectPOModal
        show={!!rejectTarget}
        onHide={() => setRejectTarget(null)}
        purchase={rejectTarget}
        onRejected={() => {
          setRejectTarget(null);
          refetchPending();
          invalidateInbox();
        }}
      />
    </Container>
    </DashboardLayout>
  );
};

export default AdminPOInbox;
